import React, { useState } from 'react';
import { encryptApiKey } from '../utils/encryption';

const ApiKeyForm = ({ username, onNavigate, onApiKeySubmit }) => {
  const [apiKey, setApiKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async () => {
    if (!apiKey.trim()) {
      setError('Please enter an API key');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // Encrypt the API key before submitting
      const encryptedApiKey = encryptApiKey(apiKey.trim());
      
      // Call the parent component's submit handler with encrypted key
      const result = await onApiKeySubmit(encryptedApiKey);
      
      if (result.success) {
        setSuccess('Your API key has been recorded. You may continue to use AI Assistant');
        setApiKey(''); // Clear the input
        
        // Navigate back to home after a short delay
        setTimeout(() => {
          onNavigate('home');
        }, 2000);
      } else {
        setError(result.error || 'The entered OpenAI API Key is invalid');
      }
    } catch (error) {
      console.error('Error submitting API key:', error);
      setError('An error occurred while validating the API key. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoBack = () => {
    onNavigate('home');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-800">OpenAI API Key</h1>
          <button
            onClick={handleGoBack}
            className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
          >
            <span className="text-2xl">×</span>
          </button>
        </div>
        <p className="text-gray-600">
          Enter your OpenAI API key to access AI feedback features. You are responsible 
          for any charges incurred with your API key.
        </p>
      </div>

      {/* API Key Form */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="space-y-4">
          <div>
            <label htmlFor="apiKey" className="block text-sm font-medium text-gray-700 mb-2">
              OpenAI API Key
            </label>
            <input
              type="password"
              id="apiKey"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter API key here"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isSubmitting}
            />
            <p className="text-xs text-gray-500 mt-1">
              Your API key will be stored securely and only used for AI feedback generation.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !apiKey.trim()}
            className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 py-2 rounded-md transition-colors duration-200"
          >
            {isSubmitting ? 'Validating...' : 'Submit API Key'}
          </button>
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Security Information */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="font-semibold text-blue-800 mb-2">🔒 Security & Privacy</h3>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• Your API key is encrypted and stored securely</li>
            <li>• Only you and the system can access your key</li>
            <li>• You can update or remove your key at any time</li>
            <li>• Your key is never shared with other users</li>
          </ul>
        </div>

        {/* Usage Information */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-semibold text-yellow-800 mb-2">💰 Usage & Billing</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• You are responsible for all API charges</li>
            <li>• Monitor your OpenAI account regularly</li>
            <li>• Set usage limits in your OpenAI dashboard</li>
            <li>• Feedback requests count against your quota</li>
          </ul>
        </div>
      </div>

      {/* How to Get API Key */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">How to Get Your OpenAI API Key</h2>
        <div className="prose text-gray-600">
          <ol className="space-y-2">
            <li>
              <strong>1. Create an OpenAI Account:</strong> Visit{' '}
              <a 
                href="https://platform.openai.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                platform.openai.com
              </a>{' '}
              and sign up for an account.
            </li>
            <li>
              <strong>2. Add Payment Method:</strong> Add a valid payment method to your account 
              to access the API.
            </li>
            <li>
              <strong>3. Generate API Key:</strong> Go to the API Keys section and create a new 
              secret key.
            </li>
            <li>
              <strong>4. Copy the Key:</strong> Copy your API key (it starts with "sk-") and 
              paste it in the form above.
            </li>
          </ol>
        </div>
      </div>

      {/* Alternative Option */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Alternative: Free Access</h2>
        <p className="text-gray-600 mb-4">
          Prefer not to use your own API key? You can participate in our research study 
          to get free access to AI feedback.
        </p>
        <button
          onClick={() => onNavigate('consent-form')}
          className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-md transition-colors duration-200"
        >
          View Consent Form
        </button>
      </div>
    </div>
  );
};

export default ApiKeyForm;